import * as React from "react";
import { useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { ICON_MAP } from "../../shared/icons";
import { axiosRequest, RequestType, successfulRequest } from "../../utils/network";
import { AxiosResponse } from "axios";

const fiatCardIcon = ICON_MAP.fiatCardIcon;
const fiatChevronIcon = ICON_MAP.fiatChevronIcon;
const fiatCryptoIcon = ICON_MAP.fiatCryptoIcon;
const fiatBankIcon = ICON_MAP.fiatBankIcon;useState


interface DepositSelectorProps {
  setShowForm: (show: boolean) => void;
  plaid_ach_enabled?: String;
}

declare global {
  interface Window {
    Plaid: any;
  }
}

const useStyles = makeStyles(() => ({
  container: {
    marginTop: "20px",
    paddingLeft: "10px",
    paddingRight: "10px",
  },
  depositTitle: {
    fontSize: "16px",
    fontWeight: 700,
  },
  cardButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #d1d5db",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "16px",
    textDecoration: "none",
    cursor: "pointer",
  },
  leftContent: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  textGroup: {},
  title: {
    fontSize: "16px",
    fontWeight: 600,
    color: "black",
  },
  subtitle: {
    fontSize: "14px",
    color: "#6b7280",
  },
  icon: {
    width: "30px",
    height: "30px",
  },
  chevron: {
    width: "24px",
    height: "24px",
    flexShrink: 0,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 0",
  },
  spinner: {
    border: "4px solid #e5e7eb",
    borderTop: "4px solid #000",
    borderRadius: "50%",
    width: "36px",
    height: "36px",
    animation: "$spin 1s linear infinite",
    marginBottom: "12px",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  loadingText: {
    fontWeight: 600,
    fontSize: "16px",
    color: "#111827",
  },  
  errorMessage: {
    color: "#EF4444",
    backgroundColor: "#FEF2F2",
    padding: "12px 16px",
    borderRadius: "8px",
    marginTop: "16px",
    marginBottom: "16px",
    fontSize: "14px",
    border: "1px solid #FEE2E2",
  },
  fiatWarning: {
    backgroundColor: "#F6AB2F",
    borderRadius: "8px",
    padding: "16px 20px",
    marginTop: "8px",
    marginBottom: "8px",
    color: "#222",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: 1.5,
  },
}));

function getCryptoDepositPath() {
  let currentPath = window.location.pathname;
  return currentPath.replace("deposit_selection", "deposit?deposit_type=crypto");
}

const DepositSelector: React.FC<DepositSelectorProps> = ({ setShowForm, plaid_ach_enabled }) => {
  const classes = useStyles();
  const plaidRef = useRef<any>(null);
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [error, setError] = useState<string | null>(null);


  const launchPlaid = async () => {
    try {

      const response = await fetch(`/fiat/plaid/get_token`);

      const data = await response.json();

      const linkToken = data.linkToken


      if (!linkToken) {
        setError("Error getting token from Plaid");
        return;
      }

      plaidRef.current = window.Plaid.create({
        token: linkToken,
        onSuccess: async (public_token: string, metadata: any) => {
          const requestBody = {
            public_token,
            account_id: metadata.account_id,
          };

          try {
            setIsSyncing(true);
            const response = await axiosRequest(
              "/fiat/plaid/create_bank_account",
              requestBody,
              RequestType.POST
            ) as AxiosResponse;
  
            if (successfulRequest(response)) {
              setShowForm(true);
            } else {
              setError("Failed to sync bank account");
              setIsSyncing(false);
              setShowForm(false);
            }
          } catch (err) {
            setError("Failed to sync bank account");
            setIsSyncing(false);
            setShowForm(false);
          }
        }
      });

      plaidRef.current.open();
    } catch (error) {
      setError("Failed to sync bank account");
      setIsSyncing(false);
      setShowForm(false);
    }
  };

  return (
    <div className={classes.container}>
      {error && (
        <div className={classes.errorMessage}>
          {error}
        </div>
      )}
      {isSyncing ? (
          <div className={classes.loadingContainer}>
            <div className={classes.spinner}></div>
            <div className={classes.loadingText}>Syncing Bank Account Information...</div>
          </div>
        ) : (
        <>
          {plaid_ach_enabled && (
            <div 
              className={classes.cardButton}
              onClick={launchPlaid}
            >
              <div className={classes.leftContent}>
                <img src={fiatBankIcon} alt="Bank Icon" className={classes.icon} />
                <div className={classes.textGroup}>
                  <div className={classes.title}>Add Bank Account</div>
                  <div className={classes.subtitle}>
                    Deposit USDC via a bank account
                  </div>
                </div>
              </div>
              <img src={fiatChevronIcon} alt="Chevron" className={classes.chevron} />
            </div>
          )}
          <div 
            className={classes.cardButton}
            onClick={() => setShowForm(true)}
          >
            <div className={classes.leftContent}>
              <img src={fiatCardIcon} alt="Card Icon" className={classes.icon} />
              <div className={classes.textGroup}>
                <div className={classes.title}>Deposit with Card or Bank</div>
                <div className={classes.subtitle}>
                  Deposit USDC via debit or credit card
                </div>
              </div>
            </div>
            <img src={fiatChevronIcon} alt="Chevron" className={classes.chevron} />
          </div>
          <div 
            className={classes.cardButton}
            onClick={() => window.location.href = getCryptoDepositPath()}
          >
            <div className={classes.leftContent}>
              <img src={fiatCryptoIcon} alt="Crypto Icon" className={classes.icon} />
              <div className={classes.textGroup}>
                <div className={classes.title}>Deposit with Crypto</div>
                <div className={classes.subtitle}>
                  Deposit USDC via the Ethereum Network
                </div>
              </div>
            </div>
            <img src={fiatChevronIcon} alt="Chevron" className={classes.chevron} />
          </div>
        </>
      )}
    </div>
  );
  
};

export default DepositSelector;
